import React from 'react';

import { formatClaimedConfidence } from '../../utils/testRigHelpers/calibration';

/**
 * Whether the model named the right thing, on the row where it named it.
 *
 * The run header reports one identification percentage. This is the same score
 * per answer, so the misses can be found and read rather than inferred from an
 * average — and the two things being compared are printed underneath, because
 * a verdict is only actionable next to the claim it was graded against.
 *
 * Five states, kept distinct on purpose:
 *   scored + graded   brand ✓ / ✗
 *   scored, no brand  the manifest names none, or the run was never asked for
 *                     one, so brand is not graded ("Brand -")
 *   graded elsewhere  the lot was graded on another of its answers — the item
 *                     closest to the manifest — so this one shows no badge
 *   not scored        a multi-item pallet or a blank line; muted, never a miss
 *   unavailable       the run's dataset has no manifest at all, so nothing was
 *                     ever graded — no badge, rather than a "not scored" chip
 *                     on every row blaming a line nobody read
 */
const CHIP = 'inline-flex rounded px-1 py-0.5 text-[9px] font-semibold';

const EXTRA_NOTES = {
  unit: 'Another unit of a product already found. Not counted.',
  hidden: 'An estimate of hidden or additional contents, as the prompt asks for. Not counted.',
  part: 'An accessory, part or packaging of a listed product. Not counted.',
  vague: 'Too vague to tell which product it is. Not counted.',
  other: "A product the manifest doesn't name, in a lot whose manifest leaves room for more. Not counted.",
};

// What an item the lot's products did not match is. A product that is not in
// the lot at all is a wrong claim: it counts against Product, and Brand too when
// its maker is not the lot's.
const ExtraChip = ({ extra }) => (
  <span
    className={`${CHIP} ${extra.counted ? 'bg-danger/10 text-danger' : 'bg-surface-2 text-muted'}`}
    title={extra.counted
      ? `Not in this lot: a wrong claim, counted against Product${extra.foreignBrand ? ' and Brand' : ''}.`
      : EXTRA_NOTES[extra.kind]}
  >
    {extra.label}
  </span>
);

// An item in a lot graded as several products: which listed product it names.
const MultiMatchBadge = ({ multi, extra }) => {
  if (multi.pending) {
    return <span className={`${CHIP} bg-surface-2 text-muted`} title="The lot's products are being judged.">Product …</span>;
  }
  if (!multi.product) {
    return extra ? <ExtraChip extra={extra} /> : (
      <span
        className={`${CHIP} bg-surface-2 text-muted`}
        title="Not matched to a listed product: another unit of one already found, an accessory, a hidden-item estimate, or a product the manifest does not list."
      >
        Not matched
      </span>
    );
  }
  const brand = multi.brandCorrect === true ? '✓' : multi.brandCorrect === false ? '✗' : '-';
  const brandClass = multi.brandCorrect === true
    ? 'bg-positive/10 text-positive'
    : multi.brandCorrect === false ? 'bg-danger/10 text-danger' : 'bg-surface-2 text-muted';
  return (
    <div className="space-y-0.5">
      <span className="inline-flex flex-wrap items-center gap-1">
        <span className={`${CHIP} ${brandClass}`}>Brand {brand}</span>
        <span className={`${CHIP} bg-positive/10 text-positive`}>Found ✓</span>
      </span>
      <div className="text-[9px] leading-snug text-muted">
        <span className="font-semibold">Matches:</span> {multi.product}
      </div>
    </div>
  );
};

const Verdict = ({ identification, formatTooltip }) => {
  if (!identification || identification.available === false) return null;
  if (identification.gradedElsewhere) return identification.extra ? <ExtraChip extra={identification.extra} /> : null;
  const tooltip = typeof formatTooltip === 'function' ? formatTooltip(identification) : undefined;

  if (!identification.scored && identification.multi) {
    return <MultiMatchBadge multi={identification.multi} extra={identification.extra} />;
  }

  if (!identification.scored) {
    return (
      <span
        className="inline-flex rounded bg-surface-2 px-1 py-0.5 text-[9px] font-semibold text-muted"
        title={tooltip}
      >
        Not scored
      </span>
    );
  }

  const {
    brandCorrect, nameF1, modelBrand, expectedBrand, productMatch, productReason, productPending,
  } = identification;
  const brandClass =
    brandCorrect === true
      ? 'bg-positive/10 text-positive'
      : brandCorrect === false
        ? 'bg-danger/10 text-danger'
        : 'bg-surface-2 text-muted';
  const brandLabel = brandCorrect === true ? '✓' : brandCorrect === false ? '✗' : '-';
  const nameClass =
    nameF1 === null
      ? 'bg-surface-2 text-muted'
      : nameF1 >= 0.75
        ? 'bg-positive/10 text-positive'
        : nameF1 >= 0.4
          ? 'bg-warn/10 text-warn'
          : 'bg-danger/10 text-danger';

  // Product is the judge's verdict. Until it lands the chip reads "…"; with no
  // judge at all (no verdict, nothing pending) the word score stands in.
  const productChip = productMatch
    ? {
        label: `Product ${productMatch === 'yes' ? '✓' : productMatch === 'no' ? '✗' : '?'}`,
        className: productMatch === 'yes'
          ? 'bg-positive/10 text-positive'
          : productMatch === 'no' ? 'bg-danger/10 text-danger' : 'bg-surface-2 text-muted',
      }
    : productPending
      ? { label: 'Product …', className: 'bg-surface-2 text-muted' }
      : { label: `Name ${nameF1 === null ? '-' : nameF1.toFixed(2)}`, className: nameClass };

  return (
    <div className="space-y-0.5" title={tooltip}>
      <span className="inline-flex flex-wrap items-center gap-1">
        <span className={`inline-flex rounded px-1 py-0.5 text-[9px] font-semibold ${brandClass}`}>
          Brand {brandLabel}
        </span>
        <span className={`inline-flex rounded px-1 py-0.5 text-[9px] font-semibold ${productChip.className}`}>
          {productChip.label}
        </span>
        {identification.extra?.counted ? <ExtraChip extra={identification.extra} /> : null}
      </span>

      {/* The brand the model gave. An empty one on a graded row is a miss:
          the model was asked and named none. Left ungraded while the manifest
          names one, the run predates the prompt asking. What it is graded
          against sits in the lot's Manifest (ground truth) column. */}
      <div className="text-[9px] leading-snug text-muted">
        <span className="font-semibold">AI brand:</span>{' '}
        {modelBrand || (
          <span className="italic">
            {brandCorrect === null && expectedBrand ? 'not recorded' : 'none given'}
          </span>
        )}
      </div>

      {/* Why the judge decided as it did — a miss is only useful with its reason. */}
      {productReason ? (
        <div className="text-[9px] leading-snug text-muted">
          <span className="font-semibold">Judge:</span> {productReason}
        </div>
      ) : null}
    </div>
  );
};

// Only on an answer the judges counted — the claims calibration is made of.
const isCountedClaim = (identification) => Boolean(
  identification.scored || identification.multi?.product || identification.extra?.counted,
);

const IdentificationBadge = ({ identification, formatTooltip }) => {
  const verdict = Verdict({ identification, formatTooltip });
  const claimed = verdict && isCountedClaim(identification) ? formatClaimedConfidence(identification.claimed) : '';
  if (!claimed) return verdict;
  return (
    <div className="space-y-0.5">
      {verdict}
      <div
        className="text-[9px] leading-snug text-muted"
        title="How sure the model said it was of this answer. The run's Product and Brand tooltips compare that with how often it was right."
      >
        <span className="font-semibold">Model said:</span> {claimed}
      </div>
    </div>
  );
};

export default IdentificationBadge;
